import React from "react";
import { Badge, Box, IconButton, Image, SimpleGrid, Text, Tooltip, Flex, useToast } from "@chakra-ui/react";
import { format as timeAgo } from "timeago.js";
import { Link } from "react-router-dom";

import { useSpaceXPaginated } from "../utils/use-space-x";
import { formatDate } from "../utils/format-date";
import Error from "./error";
import Breadcrumbs from "./breadcrumbs";
import LoadMoreButton from "./load-more-button";
import { Star } from "react-feather";

import Favorites from "./favorites";
import { ACTIONS, FAVORITES_TARGET, useFavorites, useFavoritesUpdate } from "../contexts/favorites-context";
import { toastToggleFavorite } from './toasts';
const PAGE_SIZE = 12;

export default function Launches() {
  const { data, error, isValidating, setSize, size } = useSpaceXPaginated(
    "/launches/past",
    {
      limit: PAGE_SIZE,
      order: "desc",
      sort: "launch_date_utc",
    }
  );
  //console.log(data, error);
  const favorites = useFavorites(); 
  const setFavorites = useFavoritesUpdate();

  const setFavoriteLaunches = (launch, isFavorite) => {
    setFavorites({
      type: isFavorite ? ACTIONS.DELETE : ACTIONS.ADD, 
      payload: { id:launch.flight_number, data:launch, target: FAVORITES_TARGET.LAUNCHES}
    }); 
  }
  
  return (
    <div>
      <Flex alignItems="center" justifyContent="space-between">
        <Breadcrumbs
          items={[{ label: "Home", to: "/" }, { label: "Launches" }]}
        />
       <Favorites target={FAVORITES_TARGET.LAUNCHES} />
      </Flex>
      <SimpleGrid m={[2, null, 6]} minChildWidth="350px" spacing="4">
        {error && <Error />}
        {data &&
          data
            .flat()
            .map((launch) => (
              <LaunchItem 
                launch={launch} 
                key={launch.flight_number} 
                isFavorite={launch.flight_number in favorites[FAVORITES_TARGET.LAUNCHES]}              
                setFavoriteLaunches={setFavoriteLaunches}              
              />
            ))}
      </SimpleGrid>
      <LoadMoreButton
        loadMore={() => setSize(size + 1)}
        data={data}
        pageSize={PAGE_SIZE}
        isLoadingMore={isValidating}
      />
    </div>
  );
}

const isFavoriteEqual = (prevProps, nextProps) => {
  return prevProps.isFavorite === nextProps.isFavorite;
}

// Using memo for optimization, with new data comes new renders ensuring data integrity (shouldComponentUpdate)
// With changes to our FavoritesContext (favorites) - only rerender items that have their favorite status has changed.
export const LaunchItem = React.memo(function({ launch, isFavorite, setFavoriteLaunches}) {
  //uncomment this log in order to check out react memo functionality.
  //console.log('LaunchItem has Rendered'); 
  const toast = useToast();
  return (    
    <Box
      boxShadow="md"
      borderWidth="1px"
      rounded="lg"
      overflow="hidden"
      position="relative"
    >
      <Box // improving link clickability for UX
        as={Link}
        to={`/launches/${launch.flight_number.toString()}`}
      >
        <Image
          src={
            launch.links.flickr_images[0]?.replace("_o.jpg", "_z.jpg") ??
            launch.links.mission_patch_small
          }
          alt={`${launch.mission_name} launch`}
          height={["200px", null, "300px"]}
          width="100%"
          objectFit="cover"
          objectPosition="bottom"
        />
      </Box>
      <Image
        position="absolute"
        top="5"
        right="5"
        src={launch.links.mission_patch_small}
        height="75px"
        objectFit="contain"
        objectPosition="bottom"
      />

      <Box p="6">
        <Box d="flex" alignItems="baseline">
          <Tooltip 
              placement="left"
              label={isFavorite ? "Remove from favorites" : "Add to favorites"}
          > 
            <IconButton 
              icon={<Star/>}
              position="absolute"
              bottom={65}
              right={2}
              variant="solid"
              colorScheme={isFavorite ? "orange" : "gray"} onClick={(e) => {
                e.preventDefault();   
                setFavoriteLaunches(launch, isFavorite);
                toastToggleFavorite(toast, launch, FAVORITES_TARGET.LAUNCHES, isFavorite);
              }}
            />
          </Tooltip>
          {launch.launch_success ? (
            <Badge px="2" variant="solid" colorScheme="green">
              Successful
            </Badge>
          ) : (
            <Badge px="2" variant="solid" colorScheme="red">
              Failed
            </Badge>
          )}
          <Box
            color="gray.500"
            fontWeight="semibold"
            letterSpacing="wide"
            fontSize="xs"
            textTransform="uppercase"
            ml="2"
          >
            {launch.rocket.rocket_name} &bull; {launch.launch_site.site_name}
          </Box>
        </Box>

        <Box
          mt="1"
          fontWeight="semibold"
          as="h4"
          lineHeight="tight"
          isTruncated
        >
          {launch.mission_name}
        </Box>
        <Flex>
          <Text fontSize="sm">{formatDate(launch.launch_date_utc)}</Text>
          <Text color="gray.500" ml="2" fontSize="sm">
            {timeAgo(launch.launch_date_utc)}
          </Text>
        </Flex>
      </Box>
    </Box>
  );
}, isFavoriteEqual)
